/**
 * The two states every admin screen shares: still loading, and could not load.
 *
 * WHY THIS IS SHARED RATHER THAN PER-PAGE: an operator reading a staff screen
 * needs to be able to tell "no rows" from "the read failed" at a glance, and
 * the failure has to look the same everywhere or the distinction stops being
 * learnable. A page that renders an empty table on a 500 is actively
 * misleading on a console whose whole job is reporting the state of other
 * people's money.
 *
 * A 403 gets its own branch because it is not a fault. The SPA guard only
 * checks that the operator has SOME role; the API checks that they have the
 * RIGHT one, so a support user can legitimately reach a WRITE-gated screen and
 * be refused. That should read as "not your access", not as an outage.
 */
import type { ReactNode } from 'react';
import { ApiClientError } from '@/api/client';
import { ForbiddenPage } from './ForbiddenPage';

export function AdminLoading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="grid min-h-[40vh] place-items-center">
      <div className="flex flex-col items-center gap-3">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-brand-teal border-t-transparent" />
        <span className="text-xs uppercase tracking-[0.1em] text-white/30">{label}</span>
      </div>
    </div>
  );
}

export function AdminError({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiClientError && error.status === 403) {
    return <ForbiddenPage detail="This screen needs a role your account doesn’t have. Everything else in the console still works." />;
  }

  const code = error instanceof ApiClientError ? error.error.code : 'network_error';
  const message =
    error instanceof ApiClientError
      ? error.error.message
      : error instanceof Error
        ? error.message
        : 'The request did not complete.';

  return (
    <div className="rounded-2xl border border-red-400/30 bg-red-500/[0.07] p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-red-300">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7.5v5.5M12 16.3v.1" />
          </svg>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-red-200">Couldn’t load this screen</p>
          <p className="mt-1 text-sm leading-relaxed text-white/55">{message}</p>
          <p className="mt-2 font-mono text-[11px] text-white/30">{code}</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="mt-3 rounded-lg border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs font-medium text-white/80 transition hover:bg-white/10"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Screen title + optional subtitle and right-aligned controls. */
export function AdminPageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-display text-xl font-extrabold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm leading-relaxed text-white/45">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
